import {Tooltip} from 'platform/components';

import type {ReactNode} from 'react';

import type {CellRendererProps} from '../../types/CellRendererProps';
import type {TooltipGetter} from '../../types/TooltipGetter';
import {getCellClassName} from '../../utils/getCellClassName';
import {getTooltipProps} from '../../utils/getTooltipProps';

type MetaTextValue = {primary: string; secondary: string} | null;

type MetaTextCellRendererProps = CellRendererProps<MetaTextValue> & {
  getTooltipProps?: TooltipGetter;
};

const META_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;

const parseMetaText = (cellValue: string): ReactNode[] => {
  const segments: (string | ReactNode)[] = [];
  let lastIndex = 0;

  const matches = Array.from(cellValue.matchAll(META_REGEX));

  matches.forEach((match, index) => {
    const matchStart = match.index!;
    const matchEnd = matchStart + match[0].length;

    if (matchStart > lastIndex) {
      segments.push(cellValue.substring(lastIndex, matchStart));
    }

    const name = match[1];
    const id = match[2];

    segments.push(
      <span key={`meta-${index}-${id}`} className="bg-[color:var(--color-blue-30,#e3f0ff)] inline">
        {name}
      </span>
    );

    lastIndex = matchEnd;
  });

  if (lastIndex < cellValue.length) {
    segments.push(cellValue.substring(lastIndex));
  }

  return segments;
};

export function MetaTextCellRenderer(props: MetaTextCellRendererProps) {
  const className = getCellClassName(props.column);
  const tooltipProps = getTooltipProps(props.row, {}, props.getTooltipProps);

  const cellValue = props.value?.primary ?? null;

  const renderedValue = typeof cellValue === 'string' ? parseMetaText(cellValue) : [cellValue];

  return (
    <Tooltip {...tooltipProps}>
      <div className={className}>{renderedValue}</div>
    </Tooltip>
  );
}
